import React, { useEffect, useId, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    onBuyDropTurnstileLoad?: () => void;
  }
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onBuyDropTurnstileLoad';

export const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve) => {
      if (window.turnstile) {
        resolve();
        return;
      }
      const prev = window.onBuyDropTurnstileLoad;
      window.onBuyDropTurnstileLoad = () => {
        prev?.();
        resolve();
      };
    });
  }
  return new Promise((resolve, reject) => {
    const prev = window.onBuyDropTurnstileLoad;
    window.onBuyDropTurnstileLoad = () => {
      prev?.();
      resolve();
    };
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onerror = () => reject(new Error('Failed to load CAPTCHA'));
    document.head.appendChild(script);
  });
}

type TurnstileProps = {
  onToken: (token: string | null) => void;
  resetSignal?: number;
  className?: string;
};

/**
 * Cloudflare Turnstile widget. No-ops (reports ready with empty token) when site key is unset.
 */
const Turnstile: React.FC<TurnstileProps> = ({ onToken, resetSignal = 0, className }) => {
  const reactId = useId();
  const containerId = `cf-turnstile-${reactId.replace(/:/g, '')}`;
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      onTokenRef.current(null);
      return;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        const el = document.getElementById(containerId);
        if (!el) return;
        if (widgetIdRef.current != null) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            /* ignore */
          }
          widgetIdRef.current = null;
          el.innerHTML = '';
        }
        widgetIdRef.current = window.turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
      })
      .catch(() => {
        if (!cancelled) onTokenRef.current(null);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [containerId]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || resetSignal === 0) return;
    if (widgetIdRef.current != null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* ignore */
      }
      onTokenRef.current(null);
    }
  }, [resetSignal]);

  if (!TURNSTILE_SITE_KEY) return null;

  return <div id={containerId} className={className} />;
};

export default Turnstile;
